import Router from 'next/router'
import StateStorage from 'lib/state-storage'
import { getFilterParams } from 'lib/utils/category'
import {
  clearArrayDuplicate,
  getElementStyleProperty,
} from 'lib/utils/common/commonUtils'
import { ITEMS_LIMIT } from './const'

const getSearchBarMargin = () =>
  getElementStyleProperty({
    className: 'search-bar-active',
    key: 'margin',
    pos: 0,
  })

// TECH-73 stopgap: emotional / seasonal / occasion phrases ("date night",
// "summer vibes", "office wear") don't match product text literally, so we
// expand them into real catalogue vocabulary BEFORE the query reaches the API
// (so both Algolia's keyword index and the Pinecone vector get the right words)
// and rank the semantic results by those terms (see rankByIntent). Values
// grounded in the live catalogue (dresses, blazers, knitwear, linen, co-ords,
// swim...). The durable version is server-side query expansion in
// pomelofashion/api (TECH-73).
const INTENT_EXPANSIONS = {
  // occasion
  'date night': 'cocktail dress satin slip bodycon heels',
  'night out': 'cocktail dress bodycon sequin heels',
  'first date': 'floral midi dress blouse skirt',
  'cocktail party': 'cocktail dress sequin satin bodycon heels',
  'office wear': 'blazer tailored trousers shirt midi dress',
  'work outfit': 'blazer tailored trousers shirt midi dress',
  'wedding guest': 'maxi dress midi dress floral satin formal',
  'beach wedding': 'maxi dress linen floral sandals',
  'brunch outfit': 'floral midi dress blouse knit cardigan',
  'party outfit': 'sequin bodycon cocktail dress',
  'girls trip': 'floral dress co-ord set sandals',
  'festival outfit': 'crochet crop top denim shorts co-ord',
  'beach day': 'bikini swim cover-up linen shorts sandals',
  'vacation outfit': 'linen floral maxi dress swim sandals',
  'holiday outfit': 'linen floral maxi dress sandals',
  // seasonal
  'summer vibes': 'linen floral sundress maxi shorts tank',
  'summer outfit': 'linen floral sundress maxi shorts tank',
  'winter outfit': 'knit sweater cardigan coat',
  'winter warmth': 'knit sweater cardigan coat',
  'rainy day': 'knit cardigan trousers coat',
  'spring outfit': 'floral midi dress linen cardigan',
  'autumn outfit': 'knit sweater cardigan trousers',
  'fall outfit': 'knit sweater cardigan trousers',
  // emotional / vibe
  'feeling cute': 'floral mini dress blouse skirt',
  'cozy vibes': 'knit sweater cardigan loungewear',
  'cozy night': 'knit sweater cardigan loungewear',
  'soft girl': 'floral pastel knit cardigan midi dress',
  'romantic mood': 'floral lace satin midi dress',
  'clean girl': 'tailored trousers blazer shirt',
  'old money': 'tailored blazer trousers knit',
  'feeling confident': 'bodycon blazer cocktail dress',
  'smart casual': 'blazer tailored trousers shirt knit',
  'going out': 'cocktail dress bodycon sequin satin',
  'beach outfit': 'bikini swim cover-up linen shorts sandals',
  'birthday outfit': 'sequin bodycon cocktail dress',
  'graduation outfit': 'midi dress blazer tailored formal',
  'concert outfit': 'denim crop top co-ord',
  // Thai intent phrases -> catalogue vocabulary (product names are bilingual,
  // so the English expansion still matches; validated against the live API).
  ชุดทำงาน: 'blazer tailored trousers shirt midi dress', // office / work
  ชุดออกงาน: 'maxi dress midi dress satin formal', // formal event
  ชุดไปงานแต่ง: 'maxi dress midi dress floral satin formal', // wedding guest
  ชุดไปทะเล: 'bikini swim cover-up linen shorts sandals', // beach
  ชุดเที่ยว: 'floral dress co-ord denim shorts', // going out / travel
  ชุดปาร์ตี้: 'sequin bodycon cocktail dress', // party
  เดทไนท์: 'cocktail dress satin slip bodycon', // date night
  ออกเดท: 'floral midi dress cocktail blouse', // date
  หน้าร้อน: 'linen floral sundress maxi shorts tank', // summer
  หน้าหนาว: 'knit sweater cardigan coat', // winter
  น่ารัก: 'floral mini dress blouse skirt', // cute
}

const normalizeQuery = (query) =>
  (query || '').toLowerCase().replace(/\s+/g, ' ').trim()

// Occasion / season / vibe MODIFIERS -> attribute vocabulary, for compositional
// expansion: "<modifier> <category>" ("summer dress", "party top", "office
// skirt") blends the modifier's attributes with the category the shopper named,
// so intents generalise beyond the fixed phrase list above.
const MODIFIER_VOCAB = {
  summer: 'linen floral sundress maxi lightweight pastel',
  spring: 'floral linen pastel midi',
  winter: 'knit sweater cardigan coat wool',
  autumn: 'knit sweater cardigan trousers',
  fall: 'knit sweater cardigan trousers',
  party: 'sequin bodycon satin',
  cocktail: 'cocktail satin sequin bodycon',
  office: 'blazer tailored trousers shirt smart',
  work: 'blazer tailored trousers shirt smart',
  beach: 'linen crochet swim cover-up sandals',
  holiday: 'linen floral maxi swim sandals',
  vacation: 'linen floral maxi swim sandals',
  cozy: 'knit sweater cardigan loungewear oversized',
  cosy: 'knit sweater cardigan loungewear oversized',
  cute: 'floral mini blouse pastel',
  formal: 'maxi midi satin tailored formal',
  festival: 'crochet crop denim co-ord',
  brunch: 'floral midi blouse knit',
  wedding: 'maxi midi floral satin formal',
  date: 'cocktail satin slip bodycon',
}

// Category head nouns we recognise in a "<modifier> <category>" query (singular;
// plurals are stripped before lookup).
const CATEGORY_HEADS = new Set([
  'dress',
  'top',
  'skirt',
  'shirt',
  'blouse',
  'pant',
  'trouser',
  'jean',
  'short',
  'jacket',
  'coat',
  'blazer',
  'cardigan',
  'sweater',
  'knit',
  'jumpsuit',
  'romper',
  'set',
  'shoe',
  'heel',
  'sandal',
  'boot',
  'bag',
  'swimsuit',
  'bikini',
  'tee',
  'tank',
  'crop',
])

// Modifiers meaningful enough to stand alone as a one-word intent ("summer",
// "cozy"); kept tight so a one-word product search is never hijacked.
const STANDALONE_MODIFIERS = new Set([
  'summer',
  'winter',
  'spring',
  'autumn',
  'fall',
  'party',
  'cocktail',
  'cozy',
  'cosy',
  'festival',
  'brunch',
  'formal',
])

const stripPlural = (word) => {
  if (word.endsWith('es') && CATEGORY_HEADS.has(word.slice(0, -2))) {
    return word.slice(0, -2)
  }
  if (word.endsWith('s') && CATEGORY_HEADS.has(word.slice(0, -1))) {
    return word.slice(0, -1)
  }
  return word
}

// Expansion terms for an intent query, else null. In order: (1) a curated phrase
// (exact, then the longest known phrase contained in the query, EN or Thai);
// (2) compositional "<modifier> <category>" or a standalone modifier - gated to
// short queries so an ordinary search like "black dress" is never expanded.
const getIntentExpansion = (query) => {
  const normalized = normalizeQuery(query).replace(/\bnite\b/g, 'night')
  if (!normalized) {
    return null
  }

  if (INTENT_EXPANSIONS[normalized]) {
    return INTENT_EXPANSIONS[normalized].split(' ')
  }
  // A known phrase must appear as whole words ("date night outfit"), never
  // inside another word ("update nightly"). Thai has no word gaps, so a Thai
  // phrase matches as text.
  const queryTokens = normalized.split(' ').filter(Boolean)
  const containsPhrase = (key) => {
    if (/[฀-๿]/.test(key)) {
      return normalized.includes(key)
    }
    const keyTokens = key.split(' ')
    return queryTokens.some((_, start) =>
      keyTokens.every((token, offset) => queryTokens[start + offset] === token),
    )
  }
  const phrase = Object.keys(INTENT_EXPANSIONS)
    .sort((a, b) => b.length - a.length)
    .find(containsPhrase)
  if (phrase) {
    return INTENT_EXPANSIONS[phrase].split(' ')
  }

  const tokens = normalized.split(' ').filter(Boolean)
  if (tokens.length > 3) {
    return null
  }
  const modifier = tokens.find((token) => MODIFIER_VOCAB[token])
  if (!modifier) {
    return null
  }
  const category = tokens
    .map(stripPlural)
    .find((token) => token !== modifier && CATEGORY_HEADS.has(token))
  if (category) {
    return `${MODIFIER_VOCAB[modifier]} ${category}`.split(' ')
  }
  if (tokens.length === 1 && STANDALONE_MODIFIERS.has(modifier)) {
    return MODIFIER_VOCAB[modifier].split(' ')
  }
  return null
}

// The query string sent to the API: for an intent phrase, the original words
// plus its catalogue expansion (both the keyword index and the vector get the
// right vocabulary); otherwise the query unchanged.
const expandSearchQuery = (query) => {
  const terms = getIntentExpansion(query)
  return terms ? `${normalizeQuery(query)} ${terms.join(' ')}` : query
}

const getSearchResultParams = (filter, page, query, sortData) => {
  const sortAndFilterOptions = {
    'filter[order_by]': sortData,
    ...getFilterParams(filter),
  }

  return JSON.stringify({
    params: {
      query: expandSearchQuery(query),
      'filter[skip]': page * ITEMS_LIMIT,
      'filter[limit]': ITEMS_LIMIT,
      ...sortAndFilterOptions,
    },
  })
}

const getSearchPageUrls = (searchWord) => {
  const shop = StateStorage.getShop()
  const language = StateStorage.getLanguage()
  const encodedSearchWord = encodeURIComponent(searchWord)

  return {
    href: `/search?term=${encodedSearchWord}`,
    as: `/${shop}/${language}/search/${encodedSearchWord}`,
  }
}

const goToSearchPage = (searchWord, callback) => {
  const { href, as } = getSearchPageUrls(searchWord)

  Router.push(href, as).then(() => {
    callback()
  })
}

// TECH-20: results open inside the search panel, on top of the current page.
// The history entry uses Next's own state shape, so a reload or a Back from a
// product page still lands on the full search page.
const IN_PAGE_SEARCH_KEY = 'inPageSearch'

const pushSearchResultsUrl = (searchWord, replace = false) => {
  const { href, as } = getSearchPageUrls(searchWord)
  const state = {
    url: href,
    as,
    options: {},
    [IN_PAGE_SEARCH_KEY]: searchWord,
    // the page the results are drawn over (Next's asPath is not changed)
    inPageSearchBase: Router.router?.asPath,
  }

  if (!replace && 'scrollRestoration' in window.history) {
    // The page is frozen (scrollY 0) while the panel is open; stop the
    // browser restoring that 0 on Back so toggleBodyFreezing can put the
    // shopper back where they were. Reset in restoreHistoryScrolling.
    window.history.scrollRestoration = 'manual'
  }

  window.history[replace ? 'replaceState' : 'pushState'](state, '', as)
}

// The search panel sits in the page flow above the content, so opening it
// makes the browser's scroll anchoring shift the scroll position by the
// panel's height before toggleBodyFreezing records it. Remember where the
// shopper was before the panel opened and restore exactly that on close.
let scrollBeforeSearch

const rememberScrollBeforeSearch = () => {
  scrollBeforeSearch = window.pageYOffset
}

const takeScrollBeforeSearch = () => {
  const position = scrollBeforeSearch
  scrollBeforeSearch = undefined
  return position
}

const restoreHistoryScrolling = () => {
  if (window.history.scrollRestoration === 'manual') {
    window.history.scrollRestoration = 'auto'
  }
}

// Router.beforePopState handler: Back/Forward over in-page results only
// opens or closes the panel; anything else is left to Next.
const handleInPageSearchPopState = (
  state,
  { closeResults, isResultsOpen, openResults },
) => {
  const term = state?.[IN_PAGE_SEARCH_KEY]

  if (term && state.inPageSearchBase === Router.router?.asPath) {
    openResults(term)
    return false
  }

  if (!term && isResultsOpen) {
    closeResults()
    return false
  }

  return true
}

// The search API pads its results with loose matches once the real ones run
// out (e.g. "dress": ~200 dresses, then skirts, shirts, bikinis). Until the API
// cuts that tail (TECH-18/19), keep a product only if every search word is in
// its name, category or collection, allowing the same small spelling slips
// the API (Algolia) forgives, so "dres", "jeens" or "เดรท" still find dresses
// and jeans, as on ASOS.
const toSingular = (word) => {
  if (word.length <= 3) return word
  if (word.endsWith('ies')) return `${word.slice(0, -3)}y`
  if (word.endsWith('sses')) return word.slice(0, -2)
  if (word.endsWith('s') && !word.endsWith('ss')) return word.slice(0, -1)
  return word
}

const getSearchWords = (query) =>
  (query || '').toLowerCase().split(/\s+/).filter(Boolean)

// Algolia's default: 1 typo from 4 letters, 2 typos from 8 letters
const allowedTypos = (word) => {
  if (word.length >= 8) return 2
  if (word.length >= 4) return 1
  return 0
}

// Typos counted like Algolia: insert, delete, replace, or swap two
// neighbouring letters ("skrit" → "skirt" is one typo).
const editDistance = (a, b) => {
  const rows = Array.from({ length: a.length + 1 }, (_, i) =>
    Array.from({ length: b.length + 1 }, (__, j) => (i === 0 ? j : i)),
  )
  for (let i = 1; i <= a.length; i += 1) {
    for (let j = 1; j <= b.length; j += 1) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1
      rows[i][j] = Math.min(
        rows[i - 1][j] + 1,
        rows[i][j - 1] + 1,
        rows[i - 1][j - 1] + cost,
      )
      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) {
        rows[i][j] = Math.min(rows[i][j], rows[i - 2][j - 2] + 1)
      }
    }
  }
  return rows[a.length][b.length]
}

const THAI = /[฀-๿]/

// Does a single word in the product text match the search word? Whole-word
// only \u2014 a plural-insensitive exact match, a prefix for 4+ letter words (so
// partial typing like "dres" finds "dress"), or an allowed typo. Crucially it
// never matches inside a longer word, so "red" does NOT match "gathe(red)" and
// "red dresses" never returns a black dress.
const matchesToken = (word, token, allowTypo) => {
  if (toSingular(word) === toSingular(token)) return true
  if (word.length >= 4 && token.startsWith(word)) return true
  if (!allowTypo) return false
  const typos = allowedTypos(word)
  return typos > 0 && editDistance(word, token) <= typos
}

// Thai has no spaces between words, so match a run of letters of about the
// same length instead of whole tokens.
const findThaiMatch = (word, text, allowTypo) => {
  if (text.includes(word)) return word
  const typos = allowTypo ? allowedTypos(word) : 0
  if (!typos) return null

  let best = null
  let bestDistance = typos + 1
  ;[word.length - 1, word.length, word.length + 1].forEach((length) => {
    for (let i = 0; length > 0 && i + length <= text.length; i += 1) {
      const candidate = text.slice(i, i + length)
      const distance = editDistance(word, candidate)
      if (distance < bestDistance) {
        best = candidate
        bestDistance = distance
      }
    }
  })
  return best
}

const findWordForm = (word, text, allowTypo) => {
  const lowerText = text.toLowerCase()
  if (THAI.test(word)) {
    return findThaiMatch(word, lowerText, allowTypo)
  }

  const tokens = lowerText.split(/[^a-z0-9\u00C0-\u024F]+/).filter(Boolean)
  return tokens.find((token) => matchesToken(word, token, allowTypo)) || null
}

// Returns the piece of `text` that `word` matches, or null. Tries the word as
// typed, then its singular ("dresses" → "dress"). `allowTypo` turns on typo
// tolerance ("jeens" → "jeans"); callers switch it off when the word is known
// to be spelled correctly, so e.g. "black" never matches "block".
const findSearchWordMatch = (word, text, allowTypo = true) => {
  const singular = toSingular(word)
  return (
    findWordForm(word, text, allowTypo) ||
    (singular !== word ? findWordForm(singular, text, allowTypo) : null)
  )
}

const getProductSearchText = (product) =>
  [product?.name, product?.category_link_rewrite, product?.collection_name]
    .filter(Boolean)
    .join(' ')

// Synonyms, so "trousers" finds pants, "tee" finds t-shirts, etc. (the
// Algolia synonyms config will own this server-side; this is the www stopgap).
const SYNONYM_GROUPS = [
  ['pants', 'trousers'],
  ['tee', 't-shirt', 'tshirt'],
  ['jumper', 'sweater', 'pullover'],
  ['sneakers', 'trainers'],
  ['bag', 'handbag', 'purse'],
  ['swimsuit', 'swimwear'],
  ['hoodie', 'sweatshirt'],
  ['jacket', 'coat'],
  ['hat', 'beanie'],
  ['grey', 'gray'],
]

const SYNONYMS = {}
SYNONYM_GROUPS.forEach((group) => {
  group.forEach((word) => {
    SYNONYMS[word] = group
  })
})

const expandSynonyms = (word) => SYNONYMS[word] || [word]

// A query word matches a product when the word — or any of its synonyms —
// matches, honouring the set-aware typo tolerance of findSearchWordMatch.
const wordMatchesText = (word, text, allowTypo) =>
  expandSynonyms(word).some((form) =>
    findSearchWordMatch(form, text, allowTypo),
  )

// Lenient single-product check (typo tolerance on), used by the suggestions.
const isRelatedToSearch = (product, query) => {
  const words = getSearchWords(query)
  const text = getProductSearchText(product)

  return (
    words.length > 0 && words.every((word) => wordMatchesText(word, text, true))
  )
}

// Filters a result set to products that genuinely match the query. Typo
// tolerance is decided per word across the whole set: a word gets it only when
// NO product spells it exactly. So "black dresses" against real black dresses
// requires an exact "black" (a multi-colour "Color Block" dress is dropped),
// while a true typo like "jeens" — which nothing spells exactly — still falls
// back to matching "jeans".
const filterRelatedProducts = (products, query) => {
  const words = getSearchWords(query)
  if (!words.length || !products.length) {
    return []
  }

  const texts = products.map(getProductSearchText)
  const allowTypoByWord = words.map(
    (word) => !texts.some((text) => wordMatchesText(word, text, false)),
  )

  return products.filter((product, i) =>
    words.every((word, w) =>
      wordMatchesText(word, texts[i], allowTypoByWord[w]),
    ),
  )
}

// Available items first (ASOS/SHEIN), a stable sort so order is otherwise
// unchanged. Only applied for the default relevance sort; the API owns the
// explicit New In / Price orders. The server-side conversion-weighted ranking
// (TECH-18/19) replaces this stopgap.
const isSoldOut = (product) => Boolean(product?.stock?.is_sold_out)

const demoteSoldOut = (products) =>
  [...products].sort((a, b) => Number(isSoldOut(a)) - Number(isSoldOut(b)))

// Category/type head nouns (singular), seeded from the catalogue's own slugs
// (bottoms, tops, outerwear, dresses, hats-and-hair, bags, shoes, ...). Used to
// weight the query's head noun so "hat" outranks "top" in "top hat". The full
// vocabulary + synonyms will live in Algolia server-side (see the strategy doc).
const CATEGORY_WORDS = new Set([
  'dress',
  'top',
  'tee',
  'shirt',
  'blouse',
  'knit',
  'sweater',
  'jumper',
  'cardigan',
  'skirt',
  'pant',
  'trouser',
  'short',
  'jean',
  'jacket',
  'coat',
  'blazer',
  'jumpsuit',
  'romper',
  'hat',
  'cap',
  'beanie',
  'bag',
  'shoe',
  'heel',
  'sandal',
  'boot',
])

// The head noun of a query: the last token that is a category word, else the
// last token (English puts the head noun last — "red dress", "top hat").
const headNoun = (words) => {
  const category = words.filter((w) => CATEGORY_WORDS.has(toSingular(w)))
  return category.length
    ? category[category.length - 1]
    : words[words.length - 1]
}

const HEAD_WEIGHT = 10

// How specifically a product matches the query: +HEAD_WEIGHT for matching the
// head noun, +1 for each other query word matched (strict, no typo). A product
// matching the head category scores far above one matching only a modifier.
const scoreProductSpecificity = (product, query) => {
  const words = getSearchWords(query)
  if (!words.length) {
    return 0
  }
  const text = getProductSearchText(product)
  const head = headNoun(words)
  return words.reduce((score, word) => {
    if (!wordMatchesText(word, text, false)) {
      return score
    }
    return score + (word === head ? HEAD_WEIGHT : 1)
  }, 0)
}

// Specific → hybrid: most-specific first, then in-stock, then the API's order
// (stable). Used for the default relevance sort so a thin/mixed result set
// (e.g. "top hat") leads with the closest matches instead of a raw jumble.
const rankBySpecificity = (products, query) =>
  products
    .map((product, index) => ({
      product,
      index,
      score: scoreProductSpecificity(product, query),
    }))
    .sort(
      (a, b) =>
        b.score - a.score ||
        Number(isSoldOut(a.product)) - Number(isSoldOut(b.product)) ||
        a.index - b.index,
    )
    .map((x) => x.product)

// Rank an intent query's semantic results by how many expansion terms a product
// matches (in-stock first, else the API's order, stable). Unlike
// rankBySpecificity there is no single head noun, so for "office wear" a blazer
// and a dress tie and both beat an unrelated accessory - pushing the noise
// (hair clips, balloon pants) to the bottom.
const rankByIntent = (products, terms) => {
  const needles = (terms || []).filter(Boolean)
  return products
    .map((product, index) => {
      const text = getProductSearchText(product)
      const score = needles.reduce(
        (matched, term) =>
          matched + (wordMatchesText(term, text, false) ? 1 : 0),
        0,
      )
      return { product, index, score }
    })
    .sort(
      (a, b) =>
        b.score - a.score ||
        Number(isSoldOut(a.product)) - Number(isSoldOut(b.product)) ||
        a.index - b.index,
    )
    .map((x) => x.product)
}

// --- Size-aware ranking (TECH-74 www: availability + personalization) ---

// The mainstream alpha sizes most shoppers buy. An item whose only in-stock
// sizes are fringe (extreme XXS/XXL, kids) is pushed below the in-size items
// even on a perfect text match - a merchandising rule. Numeric sizing
// (32-46, EU/JP) and one-size items are their own valid systems and are never
// demoted on this basis. Config-driven so it can be tuned per market.
const CORE_ALPHA_SIZES = new Set(['XS', 'S', 'M', 'L', 'XL'])

const normalizeSize = (size) =>
  String(size || '')
    .trim()
    .toUpperCase()

// A "popular" (buyable-by-most) size: a mainstream alpha size, or any numeric
// adult size (its own valid system). Kids sizes ("4YR") and extreme alpha
// (XXS/XXL) are the fringe we demote when nothing else is left.
const isPopularSize = (size) => {
  const value = normalizeSize(size)
  if (/\d+\s*(YR|Y|M|MTH)\b/.test(value) || /^(KIDS|BABY)/.test(value)) {
    return false // kids
  }
  if (/^(ONE|FREE)[\s-]?SIZE$|^OS$|^FREE$/.test(value)) {
    return true // one-size / free-size items fit most shoppers
  }
  if (/^\d{1,3}$/.test(value)) {
    return true // adult numeric sizing (UK/US 0-16, EU/JP 32-46, etc.)
  }
  return CORE_ALPHA_SIZES.has(value)
}

// The product's sizes that are actually in stock.
const inStockSizes = (product) =>
  (product?.sizes || [])
    .filter(
      (entry) =>
        entry?.stock &&
        !entry.stock.is_sold_out &&
        (entry.stock.quantity || 0) > 0,
    )
    .map((entry) => normalizeSize(entry.size))

// Does the product have a popular size in stock? One-size items (accessories,
// no size breakdown) keep popular status as long as they're in stock.
const hasCoreSizeInStock = (product) => {
  if (!(product?.sizes || []).length) {
    return !isSoldOut(product)
  }
  return inStockSizes(product).some(isPopularSize)
}

// Is the product in stock in one of the shopper's preferred sizes?
const availableInPreferredSize = (product, preferredSizes) => {
  if (!preferredSizes || !preferredSizes.length) {
    return false
  }
  const live = new Set(inStockSizes(product))
  return preferredSizes.some((size) => live.has(normalizeSize(size)))
}

// Relevance score for the unified ranker: intent-term matches for an intent
// query, else the head-noun-weighted specificity score.
const relevanceScore = (product, { query, intentTerms }) => {
  if (intentTerms && intentTerms.length) {
    const text = getProductSearchText(product)
    return intentTerms.reduce(
      (matched, term) => matched + (wordMatchesText(term, text, false) ? 1 : 0),
      0,
    )
  }
  return scoreProductSpecificity(product, query)
}

// The default-sort order for search results. Priority, highest first:
//   1. has a common size in stock  (no-popular-size items sink to the end,
//      even great matches)
//   2. relevance to the query
//   3. the shopper's own size (personalization) - among equally relevant items,
//      the ones in her size first; never surfaces an irrelevant item for size
//   4. in stock, then the API's order (stable)
const rankSearchResults = (
  products,
  { query, intentTerms, preferredSizes = [] } = {},
) =>
  products
    .map((product, index) => ({
      product,
      index,
      core: hasCoreSizeInStock(product),
      relevance: relevanceScore(product, { query, intentTerms }),
      fit: availableInPreferredSize(product, preferredSizes),
      live: !isSoldOut(product),
    }))
    .sort(
      (a, b) =>
        Number(b.core) - Number(a.core) ||
        b.relevance - a.relevance ||
        Number(b.fit) - Number(a.fit) ||
        Number(b.live) - Number(a.live) ||
        a.index - b.index,
    )
    .map((x) => x.product)

// Gibberish gate for the semantic fallback: a query is "real" if any token is
// Thai, or is alphabetic (2+ letters) with a vowel. "zzqxvbnm" has no vowel so
// it stays a no-result; "beach wedding outfit"/"เดรส" pass. Without a Pinecone
// score, www can't tell a weak semantic match from junk, so it only decides
// whether to trust the API's results at all (the real cut is server-side).
const isLikelyRealQuery = (query) =>
  (query || '')
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean)
    .some(
      (token) =>
        THAI.test(token) || (token.length >= 2 && /[aeiouy]/.test(token)),
    )

// ASOS-style type-ahead ("dres" → Dress, Mini Dress, Midi Dress...), built
// from the live search results because the API has no suggestions endpoint
// yet (an Algolia Query Suggestions index would replace this, TECH-18).
const SUGGESTION_STOP_WORDS = ['with', 'and', 'in', 'the', 'of', 'to', 'x']

const mostFrequent = (counts) => {
  const keys = Object.keys(counts)
  return keys.length ? keys.sort((a, b) => counts[b] - counts[a])[0] : undefined
}

const toDisplay = (text) =>
  text.replace(
    /(^|\s)([a-z])/g,
    (_, space, letter) => space + letter.toUpperCase(),
  )

const getNameWords = (product) =>
  (product?.name || '').split(' - ')[0].split(/\s+/).filter(Boolean)

const buildSearchSuggestions = (products = [], term = '', max = 8) => {
  const words = getSearchWords(term)
  const related = products.filter((product) => isRelatedToSearch(product, term))
  if (!words.length || !related.length) return []

  const lastWord = words[words.length - 1]
  const matchesLastWord = (token) =>
    Boolean(findSearchWordMatch(lastWord, token.toLowerCase()))

  if (THAI.test(term)) {
    // Thai names have no word gaps: offer the term and short name heads
    const heads = related
      .map((product) => getNameWords(product)[0] || '')
      .filter((head) => head.length <= 18 && matchesLastWord(head))
    return [...new Set([term.trim(), ...heads])].slice(0, max)
  }

  // the catalogue's spelling of what was typed ("dres" → "dress")
  const spellings = {}
  related.forEach((product) =>
    getNameWords(product)
      .filter(matchesLastWord)
      .forEach((token) => {
        const key = toSingular(token.toLowerCase().replace(/[^a-z]/g, ''))
        spellings[key] = (spellings[key] || 0) + 1
      }),
  )
  const corrected = [...words.slice(0, -1), mostFrequent(spellings) || lastWord]
  const correctedPhrase = corrected.join(' ')

  // most common word just before it in product names ("mini dress")
  const before = {}
  related.forEach((product) => {
    const tokens = getNameWords(product).map((token) => token.toLowerCase())
    tokens.forEach((token, index) => {
      const previous = tokens[index - 1]
      if (
        index > 0 &&
        matchesLastWord(token) &&
        /^[a-z]{3,}$/.test(previous) &&
        !SUGGESTION_STOP_WORDS.includes(previous) &&
        !words.includes(previous)
      ) {
        before[previous] = (before[previous] || 0) + 1
      }
    })
  })
  const phrases = Object.keys(before)
    .sort((a, b) => before[b] - before[a])
    .map((word) =>
      [...corrected.slice(0, -1), word, corrected[corrected.length - 1]].join(
        ' ',
      ),
    )

  return [correctedPhrase, ...phrases].slice(0, max).map(toDisplay)
}

const isInPageSearchEntry = () =>
  typeof window !== 'undefined' &&
  Boolean(window.history.state?.[IN_PAGE_SEARCH_KEY])

// A new search while results are already showing replaces the entry, so one
// Back always returns to the page the shopper searched from.
const openSearchResults = (searchWord, openResults) => {
  pushSearchResultsUrl(searchWord, isInPageSearchEntry())
  openResults(searchWord)
}

const closeSearchPanel = ({ closeSearchModal, query }) => {
  if (query && isInPageSearchEntry()) {
    // the popstate handler in the header closes the panel
    window.history.back()
    return
  }

  closeSearchModal()
}

const SEARCH_HISTORY_DETAILS = 'recentSearchListV2'

const clearRecentSearch = () => {
  StateStorage.removeCookie('recentSearchList')
  StateStorage.removeCookie(SEARCH_HISTORY_DETAILS)
}

const setRecentSearchItems = (recentSearchList) => {
  const searchItemList = recentSearchList.toString()
  StateStorage.setCookie('recentSearchList', searchItemList)
  StateStorage.setCookie(
    SEARCH_HISTORY_DETAILS,
    JSON.stringify(recentSearchList),
  )
}

const getRecentSearchItems = () => {
  const serializedState = StateStorage.getCookie('recentSearchList')

  if (!serializedState) {
    return undefined
  }

  // Older open pages still write/clear the original cookie. Only use the
  // lossless list while it agrees with that cookie; never resurrect old history.
  try {
    const details = JSON.parse(
      StateStorage.getCookie(SEARCH_HISTORY_DETAILS) || 'null',
    )
    if (
      Array.isArray(details) &&
      details.every((term) => typeof term === 'string') &&
      details.toString() === serializedState
    ) {
      return details.slice(0, 10)
    }
  } catch (error) {
    // An absent or malformed optional cookie must not break search.
  }

  const recentSearchList = serializedState.split(',')
  if (recentSearchList.length > 10) {
    recentSearchList.splice(10)
    setRecentSearchItems(recentSearchList)
  }

  return recentSearchList
}

const storeRecentSearchTerm = (term) => {
  let searchItem = getRecentSearchItems() || []
  searchItem.unshift(term)

  if (searchItem.includes(term)) {
    searchItem = clearArrayDuplicate(searchItem)
  }

  if (searchItem.length > 10) {
    searchItem.pop()
  }

  setRecentSearchItems(searchItem)
}

const storeTheSearchWord = (searchWord, setSearchWord) => {
  storeRecentSearchTerm(searchWord)
  if (typeof window !== 'undefined') {
    sessionStorage.setItem('recent-search-word', searchWord)
  }
  setSearchWord(searchWord)
}

const generateKeywordList = (length) =>
  Array.from({ length }, (_, i) => `Keyword ${i + 1}`)

const getKeywordList = (isRecentSearchListValid, recentSearchList) => {
  const generatedKeywords = generateKeywordList(5)
  const keywordList = isRecentSearchListValid
    ? recentSearchList
    : generatedKeywords

  return keywordList
}

const checkHasFilterQuickSize = (filter, isPhone, quickSizes) => {
  const isFilterValid = Object.values(filter).some(
    ({ values }) => values.length > 0,
  )

  return isPhone ? isFilterValid && quickSizes : isFilterValid
}

export {
  IN_PAGE_SEARCH_KEY,
  buildSearchSuggestions,
  checkHasFilterQuickSize,
  clearRecentSearch,
  closeSearchPanel,
  demoteSoldOut,
  expandSearchQuery,
  expandSynonyms,
  getIntentExpansion,
  isLikelyRealQuery,
  filterRelatedProducts,
  findSearchWordMatch,
  getKeywordList,
  getProductSearchText,
  getRecentSearchItems,
  getSearchBarMargin,
  getSearchPageUrls,
  getSearchResultParams,
  getSearchWords,
  goToSearchPage,
  handleInPageSearchPopState,
  isInPageSearchEntry,
  isRelatedToSearch,
  openSearchResults,
  pushSearchResultsUrl,
  rankByIntent,
  rankBySpecificity,
  rankSearchResults,
  hasCoreSizeInStock,
  availableInPreferredSize,
  scoreProductSpecificity,
  rememberScrollBeforeSearch,
  restoreHistoryScrolling,
  takeScrollBeforeSearch,
  storeTheSearchWord,
}
